// Persistence for /home-key progress, following the established site pattern
// (see KonNimi / Number Sense): an anonymous per-device id in localStorage,
// state mirrored to localStorage always, and to the Firestore doc
// homeKeyProgress/{userId} when Firebase is configured. Every Firestore touch
// is guarded by isFirebaseConfigured so the course degrades gracefully to
// device-local progress.
//
// State shape:
//   { completed: { [lessonId]: true },
//     keepers: { [keeperId]: { phrase, savedAt, lessonId } },
//     currentLessonId, updatedAt }

import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db, isFirebaseConfigured } from "./firebase";

const USER_KEY = "homeKeyUserId";
const LOCAL_KEY = "homeKeyProgress";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// The anonymous per-device id, minted on first visit and kept in localStorage.
export const getUserId = () => {
  try {
    let id = localStorage.getItem(USER_KEY);
    if (!id) {
      id = newId();
      localStorage.setItem(USER_KEY, id);
    }
    return id;
  } catch {
    return newId(); // private mode / quota — a per-session id is fine
  }
};

// A fresh, empty progress state.
export const emptyProgress = () => ({
  completed: {},
  keepers: {},
  practice: {},
  currentLessonId: null,
  updatedAt: 0,
});

// Coerce anything (a Firestore doc, parsed JSON, undefined) into a well-formed
// progress state so callers never see missing fields.
export const normalizeProgress = (raw) => {
  if (!raw || typeof raw !== "object") return emptyProgress();
  return {
    completed: raw.completed || {},
    keepers: raw.keepers || {},
    practice: raw.practice || {},
    currentLessonId: raw.currentLessonId || null,
    updatedAt: raw.updatedAt || 0,
  };
};

// Read progress from localStorage; corrupt or missing data → empty state.
export const loadLocal = () => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? normalizeProgress(JSON.parse(raw)) : emptyProgress();
  } catch {
    return emptyProgress();
  }
};

// Mirror progress to localStorage (best-effort; quota errors are ignored).
export const saveLocal = (state) => {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private-mode */
  }
};

// Subscribe to this user's progress. With Firebase configured, listens to the
// Firestore doc (so a second device stays in sync); otherwise delivers the
// localStorage copy once. Always returns an unsubscribe function.
export const subscribeProgress = (userId, cb) => {
  if (!isFirebaseConfigured || !db) {
    cb(loadLocal());
    return () => {};
  }
  const ref = doc(db, "homeKeyProgress", userId);
  return onSnapshot(
    ref,
    (snap) => {
      cb(snap.exists() ? normalizeProgress(snap.data()) : loadLocal());
    },
    () => {
      // Firestore unreachable — fall back to whatever we have locally.
      cb(loadLocal());
    }
  );
};

// Save progress: stamps updatedAt, writes localStorage, and (when configured)
// merges into the Firestore doc. Returns the stamped state.
export const saveProgress = (userId, state) => {
  const next = { ...normalizeProgress(state), updatedAt: Date.now() };
  saveLocal(next);
  if (isFirebaseConfigured && db) {
    setDoc(doc(db, "homeKeyProgress", userId), next, { merge: true }).catch(
      () => {}
    );
  }
  return next;
};
